import { useCallback, useEffect, useRef, useState } from 'react'
import { subscribe, useSnapshot } from 'valtio'
import {
  AssessmentActions,
  AssessmentEventType,
  AssessmentStatus,
  createAssessmentEvent,
  createUserAssessment,
  getAssessmentById,
  getUserAssessmentById,
  updateUserAssessment,
  UserAssessment,
  getUserAssessmentScoring,
  getTestAdministrationById,
  getStudentById,
  convertStudentAccommodationToSettings,
  calculateMaximumAdjustedTimeLimit,
} from '@app/firebase/api'
import { trackEvent, trackReviewEvent } from '@app/services/eventChain'
import {
  CurrentTestState,
  CurrentUserAssessmentState,
  CurrentTimerState,
  pauseTimer,
  resumeTimer,
  IdleDetectorState,
  setItemsResponses,
  setCurrentItemResponse,
  setUserAssessmentData,
  setUserAssessmentStatus,
  setStartedTimestamp,
  setScoring,
  setHideConstraints,
  ScoringState,
  setGroupTestScoring,
  setProgram,
  setSubject,
  setCourseCategory,
  setPreviewMode,
  setOnItemResponseChanged,
  setQuestionPreviewModeData,
} from '@app/storage'
import { AssessmentItemResponse } from '@app/models'
import { Program, TimerStatus, UseUserAssessmentFetchType, UserAuthData } from '@app/types'
import { useTestPartFinishedCheck, useUpdatingRef } from '../hooks/common'
import { useDefineTimeLimit } from '../hooks/timer'
import { FirebaseError } from 'firebase/app'
import { FirebaseErrorCodes, ROUTES, StorageKeys } from '@app/constants'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { Timestamp } from 'firebase/firestore'
import { generateGroupUserAssessmentPayload, generateInitialUserAssessmentPayload } from '@app/firebase/utils'
import { LocalStorageService } from '@app/services'
import { getAnswerKeyItemsByPracticeTest, getResponsesByPracticeTest } from '@app/services/groupReview'
import { generateRefScope } from '../refScope'
import { shouldUsePreviewAnswerKeys } from './authMode'
import { clearMarkForReviewForAssessment } from '../utils'

export const usePassedTimeRef = () => {
  const maxTime = useDefineTimeLimit()
  const { remainedTime, passedTime, isUnlimited } = useSnapshot(CurrentTimerState)

  const addedTime = CurrentUserAssessmentState?.addedTime ?? 0
  const totalTime = maxTime + addedTime

  const passedTimeRef = useUpdatingRef<number>(
    totalTime && !isUnlimited ? totalTime - (remainedTime ?? totalTime) : passedTime,
  )

  return passedTimeRef
}

/**
 * Fetch user-assessment data from firestore.
 */
export const useUserAssessmentFetch = (): UseUserAssessmentFetchType => {
  const { userAssessmentId, userId } = useSnapshot(CurrentUserAssessmentState)
  const { groupMode } = useSnapshot(ScoringState)
  const [fetchedAssessment, setFetchedAssessment] = useState<UserAssessment>(null)
  const [loading, setLoading] = useState(false)
  const [searchParams] = useSearchParams()

  const navigate = useNavigate()

  useEffect(() => {
    if (!userAssessmentId) {
      return
    }

    const hideInfoBoxes = searchParams.get('noinfoboxes') === 'true'

    const createAssessmentForUser = async (userId: string) => {
      const assessmentId = searchParams.get('id')
      const schoolYear = searchParams.get('schoolYear')
      const attempt = searchParams.get('attempt')
      const testAdminId = searchParams.get('testAdminId')

      const assessment = await getAssessmentById(assessmentId)

      // Fetch test administration settings if available
      let testAdminSettings = null
      if (testAdminId) {
        const testAdmin = await getTestAdministrationById(testAdminId)
        testAdminSettings = testAdmin?.settings || null
      }

      // Fetch student accommodation settings
      let studentSettings = null
      try {
        const student = await getStudentById(userId)
        if (student?.timeAccomodation) {
          studentSettings = convertStudentAccommodationToSettings(student.timeAccomodation)
        }
      } catch (error) {
        console.warn('Could not fetch student accommodations:', error)
        // Continue without student accommodations
      }

      // Calculate maximum adjusted time limit between test admin and student
      const adjustedTimeLimit = calculateMaximumAdjustedTimeLimit(
        assessment?.settings?.timeLimit,
        testAdminSettings,
        studentSettings,
      )

      // Since we're now storing the final adjusted time in timeLimits,
      // we set addedTime to 0 to prevent double-counting.
      // The timeLimits field now contains the total time including all accommodations.
      const addedTimeInMinutes = 0

      try {
        await createUserAssessment(
          generateInitialUserAssessmentPayload({
            schoolYear,
            userId,
            attempt: Number(attempt) ?? 1,
            assessmentId,
            hideInfoBoxes,
            assessmentSettings: {
              timeLimits: adjustedTimeLimit, // Use adjusted time (0 for unlimited) instead of base time
              isResumable: assessment.settings.isResumable,
            },
            addedTime: addedTimeInMinutes, // Set to 0 since time is already adjusted in timeLimits
          }),
        )
      } catch {
        navigate(ROUTES.error500)
      }
    }

    const fetchUserAssessment = async () => {
      setLoading(true)
      // Launch params come from the URL, so set them once for both the existing and the create path.
      setProgram(searchParams.get('program'))
      setSubject(searchParams.get('subject'))
      setCourseCategory(searchParams.get('courseCategory'))

      try {
        // Check if we're in demo mode to always fetch from server
        const isDemoMode = searchParams.get('origin') === 'demo'

        // Get assessment data from server to ensure the latest data, especially after a reset.
        const userAssessment = await getUserAssessmentById(userAssessmentId, isDemoMode || true)

        // Check if accommodations have changed (only if test not completed)
        if (userAssessment.status !== AssessmentStatus.completed) {
          const testAdminId = searchParams.get('testAdminId')
          const assessmentId = userAssessment.assessmentId

          // Fetch base assessment settings
          const assessment = await getAssessmentById(assessmentId)

          // Fetch test administration settings if available
          let testAdminSettings = null
          if (testAdminId) {
            const testAdmin = await getTestAdministrationById(testAdminId)
            testAdminSettings = testAdmin?.settings || null
          }

          // Fetch student accommodation settings
          let studentSettings = null
          try {
            const student = await getStudentById(userAssessment.userId)
            if (student?.timeAccomodation) {
              studentSettings = convertStudentAccommodationToSettings(student.timeAccomodation)
            }
          } catch (error) {
            console.warn('Could not fetch student accommodations for update check:', error)
          }

          // Calculate what the time limit should be
          const adjustedTimeLimit = calculateMaximumAdjustedTimeLimit(
            assessment.settings.timeLimit,
            testAdminSettings,
            studentSettings,
          )

          // Since timeLimits now contains the final adjusted time,
          // addedTime should always be 0 to prevent double-counting
          const calculatedAddedTime = 0

          // Check if either timeLimits or addedTime needs updating
          const needsUpdate =
            calculatedAddedTime !== userAssessment.addedTime ||
            adjustedTimeLimit !== userAssessment.assessmentSettings?.timeLimits

          if (needsUpdate) {
            await updateUserAssessment(userAssessmentId, {
              addedTime: calculatedAddedTime, // Always 0 now
              assessmentSettings: {
                ...userAssessment.assessmentSettings,
                timeLimits: adjustedTimeLimit, // Update to adjusted time limit
              },
            })
            userAssessment.addedTime = calculatedAddedTime
            if (userAssessment.assessmentSettings) {
              userAssessment.assessmentSettings.timeLimits = adjustedTimeLimit
            }
          }
        }

        setItemsResponses(userAssessment.itemResponses ?? [])
        setFetchedAssessment(userAssessment)
        setUserAssessmentData(userAssessment)

        // A retake must not inherit the previous attempt's review flags: the localStorage key
        // is only `${userId}/${assessmentId}`, so it survives a reset. Done here rather than on
        // the new -> started transition because QuestionContextMenuProvider mounts outside the
        // loading gate in Test.tsx and hydrates before the test starts. Completed attempts are
        // untouched, so flags still show while the learner reviews their submitted answers.
        if (userAssessment.status === AssessmentStatus.new) {
          clearMarkForReviewForAssessment(userAssessment.userId, userAssessment.assessmentId)
        }

        if (hideInfoBoxes && !userAssessment.hideInfoBoxes) {
          setHideConstraints(hideInfoBoxes)
        }

        if (userAssessment.status === AssessmentStatus.completed) {
          // Fetch scoring data, if student completed the test.
          const scoring = await getUserAssessmentScoring(userAssessmentId)
          setScoring(scoring)
        }

        setLoading(false)
      } catch (error) {
        if (error instanceof FirebaseError) {
          if (error.code === FirebaseErrorCodes.NotFound) {
            const authData = LocalStorageService.getValue<UserAuthData>(StorageKeys.AuthData)

            if (!searchParams || !authData) {
              navigate(ROUTES.notFound)
              return
            }

            await createAssessmentForUser(authData.user.uid)

            try {
              // Get assessment from default source after creating new assessment.
              // This is needed to ensure consistency with offline mode if that is the default.
              const userAssessment = await getUserAssessmentById(userAssessmentId)

              setItemsResponses(userAssessment.itemResponses ?? [])
              setFetchedAssessment(userAssessment)
              setUserAssessmentData(userAssessment)

              // Same fresh-attempt guard as above: a doc created here is always a new attempt.
              if (userAssessment.status === AssessmentStatus.new) {
                clearMarkForReviewForAssessment(userAssessment.userId, userAssessment.assessmentId)
              }
            } catch {
              navigate(ROUTES.error500)
            } finally {
              setLoading(false)
            }
          }

          setLoading(false)
          if (error.code === FirebaseErrorCodes.PermissionDenied) navigate(ROUTES.permissionDenied)
        } else {
          navigate(ROUTES.error500)
        }
      }
    }

    const fetchGroupScoring = async () => {
      const program = searchParams.get('program') as Program
      const subject = searchParams.get('subject')
      const contentId = searchParams.get('contentId')
      const teacherId = searchParams.get('teacherId')
      const classId = searchParams.get('classId')
      const schoolId = searchParams.get('schoolId')
      const districtId = searchParams.get('districtId')
      const preview = searchParams.get('preview')?.toLowerCase() === 'true'
      const questionPreviewId = searchParams.get('questionPreviewId') || null

      try {
        if (shouldUsePreviewAnswerKeys({ preview, groupMode, userId })) {
          // Preview mode: only fetch answer keys without student response data
          const correctAnswers = await getAnswerKeyItemsByPracticeTest(program, subject, contentId)
          setGroupTestScoring(null, correctAnswers)
          setPreviewMode(true)
          setQuestionPreviewModeData(questionPreviewId)
        } else {
          // Normal group review mode with percentages
          const data = await getResponsesByPracticeTest(
            program,
            subject,
            generateRefScope({ classId, teacherId, schoolId, districtId }),
            contentId,
          )
          const correctAnswers = await getAnswerKeyItemsByPracticeTest(program, subject, contentId)
          setGroupTestScoring(data, correctAnswers)
          setPreviewMode(false)
        }
      } catch (error) {
        console.warn(error)
      }

      const userAssessment = generateGroupUserAssessmentPayload(searchParams.get('id'))
      setItemsResponses(userAssessment.itemResponses ?? [])
      setFetchedAssessment(userAssessment)
      setUserAssessmentData(userAssessment)
      setProgram(program)
      setSubject(subject)
      setHideConstraints(true)
      trackReviewEvent({
        contentId,
        courseCategory: searchParams.get('courseCategory'),
        districtId,
        organizationId: searchParams.get('organizationId'),
        classId,
        schoolId,
        teacherId,
        origin: searchParams.get('origin'),
        preview,
        questionPreviewId,
      })
    }

    if (groupMode) {
      fetchGroupScoring()
      return
    }

    fetchUserAssessment()
  }, [navigate, userAssessmentId, userId, groupMode, searchParams])

  return { userAssessment: fetchedAssessment, loading }
}

/**
 * Start new user-assessment.
 */
export const useUserAssessmentStart = (userAssessment: UserAssessment): boolean => {
  const { currentTest, currentTestPart, isStarted } = useSnapshot(CurrentTestState)
  const { assessmentSettings, program, subject } = useSnapshot(CurrentUserAssessmentState)
  const { isIdle } = useSnapshot(IdleDetectorState)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    if (!currentTest || !currentTestPart || !userAssessment || !assessmentSettings) return

    if (!isStarted) {
      if (userAssessment.status === AssessmentStatus.completed) {
        setLoading(false)
      }

      return
    }

    if (isIdle) return

    const isResumable = CurrentUserAssessmentState.assessmentSettings.isResumable

    if (
      CurrentUserAssessmentState.status === AssessmentStatus.new ||
      (isResumable && CurrentUserAssessmentState.status === AssessmentStatus.paused)
    ) {
      console.log('[useUserAssessmentStart] starting assessment...')
      const startedTimestamp = Timestamp.fromDate(new Date())

      updateUserAssessment(CurrentUserAssessmentState.userAssessmentId, {
        status: AssessmentStatus.started,
        startedTimestamp,
      }).then(() => {
        setUserAssessmentStatus(AssessmentStatus.started)
        setStartedTimestamp(startedTimestamp)
        setLoading(false)
        createAssessmentEvent({
          action: AssessmentActions.started,
          type: AssessmentEventType.assessmentEvent,
          passedTime: 0,
          data: {
            program,
            subject,
          },
        })
        trackEvent({
          action: AssessmentActions.started,
          type: AssessmentEventType.assessmentEvent,
          passedTime: 0,
          data: {
            program,
            subject,
          },
        })
      })
      return
    }

    const itemResponses = userAssessment.itemResponses
    const lastViewedItemId = userAssessment.lastViewedItemId

    if (!lastViewedItemId || !itemResponses.length) {
      setLoading(false)
      return
    }

    const lastViewedItemResponse = itemResponses.find(itemRes => itemRes.id === lastViewedItemId)

    if (!lastViewedItemResponse) {
      setLoading(false)
      return
    }

    setCurrentItemResponse(lastViewedItemResponse as AssessmentItemResponse)
    setLoading(false)
  }, [currentTest, currentTestPart, userAssessment, assessmentSettings, isIdle, isStarted, program, subject])

  return loading
}

/**
 * Save updated items responses and passed time to the firestore.
 */
export const useUserAssessmentResponseUpdater = (): void => {
  const { itemsResponses, currentItemResponse } = useSnapshot(CurrentTestState)
  const { userAssessmentId, assessmentId, status, assessmentSettings, program, subject } =
    useSnapshot(CurrentUserAssessmentState)
  const settingsTimeLimits = assessmentSettings?.timeLimits

  const previousItemId = useRef('')

  const passedTimeRef = usePassedTimeRef()

  useEffect(() => {
    if (!assessmentId) return

    const unsubscribe = subscribe(CurrentTestState.itemsResponses, ops => {
      if (status === AssessmentStatus.new) return
      if (
        status === AssessmentStatus.started &&
        CurrentTestState.itemsResponses.length &&
        // If no time limits or if time passed, track changes
        (passedTimeRef.current > 0 || settingsTimeLimits === 0)
      ) {
        console.log(
          '[useUserAssessmentTimeTracker] tracking assessment time...',
          userAssessmentId,
          status,
          ops,
          passedTimeRef.current,
        )

        updateUserAssessment(userAssessmentId, {
          passedTime: passedTimeRef.current,
          itemResponses: CurrentTestState.itemsResponses,
        })
      }
    })

    return unsubscribe
  }, [
    assessmentId,
    userAssessmentId,
    status,
    settingsTimeLimits,
    passedTimeRef,
    itemsResponses,
    program,
    subject,
  ])

  useEffect(() => {
    if (!assessmentId) return

    // Set up callback for when student actually answers/changes an answer
    const triggerAnsweredEvent = () => {
      if (status === AssessmentStatus.new) return
      if (
        status === AssessmentStatus.started &&
        CurrentTestState.itemsResponses.length &&
        // If no time limits or if time passed, track changes
        (passedTimeRef.current > 0 || settingsTimeLimits === 0)
      ) {
        createAssessmentEvent({
          action: AssessmentActions.answered,
          passedTime: passedTimeRef.current,
          type: AssessmentEventType.assessmentEvent,
          data: {
            program,
            subject,
          },
        })
        trackEvent({
          action: AssessmentActions.answered,
          passedTime: passedTimeRef.current,
          type: AssessmentEventType.assessmentEvent,
          data: {
            program,
            subject,
          },
        })
      }
    }

    setOnItemResponseChanged(triggerAnsweredEvent)

    return () => {
      setOnItemResponseChanged(null)
    }
  }, [
    assessmentId,
    userAssessmentId,
    status,
    settingsTimeLimits,
    passedTimeRef,
    itemsResponses,
    program,
    subject,
  ])

  useEffect(() => {
    if (!userAssessmentId || !currentItemResponse?.id) {
      return
    }

    const lastViewedItemId = CurrentTestState?.currentItemResponse?.id

    if (!lastViewedItemId || previousItemId.current === lastViewedItemId) {
      return
    }

    if (!previousItemId.current) {
      previousItemId.current = lastViewedItemId
      return
    }

    if (CurrentUserAssessmentState.status === AssessmentStatus.started) {
      console.log(
        '[useUserAssessmentTimeTracker] saving last viewed item...',
        userAssessmentId,
        lastViewedItemId,
      )
      updateUserAssessment(userAssessmentId, { lastViewedItemId }).then(() => {
        previousItemId.current = lastViewedItemId
      })
    }
  }, [userAssessmentId, currentItemResponse?.id])
}

/**
 * When user-assessment report is generated, save it to the firestore.
 */
export const useUserAssessmentReporter = (): void => {
  const { currentTestPartResponse } = useSnapshot(CurrentTestState)
  const { assessmentId, userAssessmentId } = useSnapshot(CurrentUserAssessmentState)

  const xmlReport = currentTestPartResponse[0]

  useEffect(() => {
    if (!assessmentId || !userAssessmentId) return

    if (xmlReport && CurrentUserAssessmentState.status === AssessmentStatus.started) {
      console.log('[useUserAssessmentSubmit] submitting assessment report...')
      updateUserAssessment(userAssessmentId, { responseXml: xmlReport })
    }
  }, [assessmentId, xmlReport, userAssessmentId])
}

/**
 * When current test part becomes finished, mark user-assessment as "completed".
 */
export const useUserAssessmentSubmitter = (): void => {
  const passedTimeRef = usePassedTimeRef()

  const isTestPartFinished = useTestPartFinishedCheck()
  const { userAssessmentId, assessmentId, program, subject } = useSnapshot(CurrentUserAssessmentState)

  useEffect(() => {
    if (!userAssessmentId || !assessmentId) return

    const submitAssessment = async () => {
      if (CurrentUserAssessmentState.status === AssessmentStatus.started && isTestPartFinished) {
        console.log('[useUserAssessmentSubmit] completing assessment...')

        pauseTimer()

        createAssessmentEvent({
          action: AssessmentActions.completed,
          passedTime: passedTimeRef.current,
          type: AssessmentEventType.assessmentEvent,
          data: {
            program,
            subject,
          },
        })
        trackEvent({
          action: AssessmentActions.completed,
          passedTime: passedTimeRef.current,
          type: AssessmentEventType.assessmentEvent,
          data: {
            program,
            subject,
          },
        })

        await updateUserAssessment(userAssessmentId, {
          status: AssessmentStatus.completed,
          passedTime: passedTimeRef.current,
        })

        setUserAssessmentStatus(AssessmentStatus.completed)
      }
    }

    submitAssessment()
  }, [isTestPartFinished, passedTimeRef, userAssessmentId, assessmentId, program, subject])
}

/**
 * Pause both timer and resumable user-assessment.
 * @returns Callback to pause timer.
 */
export const useUserAssessentPauseCallback = (): (() => void) => {
  const passedTimeRef = usePassedTimeRef()

  const { userAssessmentId, assessmentId, program, subject } = useSnapshot(CurrentUserAssessmentState)

  const onPause = useCallback(async () => {
    if (assessmentId && !CurrentUserAssessmentState.assessmentSettings?.isResumable) {
      return
    }

    if (CurrentUserAssessmentState.status === AssessmentStatus.started) {
      console.log('[useUserAssessentPauseCallback] pausing assessment...', passedTimeRef.current)
      pauseTimer()
      createAssessmentEvent({
        action: AssessmentActions.paused,
        passedTime: passedTimeRef.current,
        type: AssessmentEventType.assessmentEvent,
        data: {
          program,
          subject,
        },
      })
      trackEvent({
        action: AssessmentActions.paused,
        passedTime: passedTimeRef.current,
        type: AssessmentEventType.assessmentEvent,
        data: {
          program,
          subject,
        },
      })
      await updateUserAssessment(userAssessmentId, {
        status: AssessmentStatus.paused,
        passedTime: passedTimeRef.current,
      })
      setUserAssessmentStatus(AssessmentStatus.paused)
    }
  }, [assessmentId, userAssessmentId, passedTimeRef, program, subject])

  return onPause
}

/**
 * Resume both timer and user-assessment.
 * @returns Callback to resume timer.
 */
export const useUserAssessentResumeCallback = (): (() => void) => {
  const { userAssessmentId, program, subject } = useSnapshot(CurrentUserAssessmentState)
  const { timerStatus } = useSnapshot(CurrentTimerState)
  const passedTimeRef = usePassedTimeRef()

  const onResume = useCallback(async () => {
    // Check timer status before resuming.
    if (timerStatus === TimerStatus.ongoing) return

    console.log('[useUserAssessentResumeCallback] resuming assessment...')
    resumeTimer()
    await updateUserAssessment(userAssessmentId, { status: AssessmentStatus.started })
    setUserAssessmentStatus(AssessmentStatus.started)
    createAssessmentEvent({
      action: AssessmentActions.resumed,
      passedTime: passedTimeRef.current,
      type: AssessmentEventType.assessmentEvent,
      data: {
        program,
        subject,
      },
    })
    trackEvent({
      action: AssessmentActions.resumed,
      passedTime: passedTimeRef.current,
      type: AssessmentEventType.assessmentEvent,
      data: {
        program,
        subject,
      },
    })
  }, [timerStatus, userAssessmentId, passedTimeRef, program, subject])

  return onResume
}

/**
 * Track user-assessment updates and save them to the firestore.
 */
export const useUserAssessentUpdater = (): void => {
  useUserAssessmentReporter()
  useUserAssessmentResponseUpdater()
  useUserAssessmentSubmitter()
}
